import type { ApiClient } from '../client'
import type {
  AuditLogEntry,
  BillingUsage,
  Branding,
  CheckoutPlan,
  ConnectState,
  DomainChallenge,
  DomainVerification,
  Integration,
  ListAuditLogQuery,
  Subscription,
  UpdateBrandingRequest,
} from '../types'

/** src/modules/branding/controllers/branding.controller.ts — the owner-scoped routes. */
export function brandingAdminEndpoints(api: ApiClient) {
  return {
    get: () => api.get<Branding>('branding'),
    update: (body: UpdateBrandingRequest) => api.put<Branding>('branding', body),
    requestVerification: () => api.post<DomainChallenge>('branding/domain/verify-request'),
    verify: () => api.post<DomainVerification>('branding/domain/verify'),
  }
}

/**
 * src/modules/integrations/controllers/integrations.controller.ts
 *
 * `connect` returns an OAuth state and nothing else: no callback and no provider credentials exist
 * in this build (BE-3), so the UI never turns it into a redirect.
 */
export function integrationsEndpoints(api: ApiClient) {
  return {
    list: () => api.getItems<Integration>('integrations'),
    connectState: (provider: string) =>
      api.get<ConnectState>(`integrations/${encodeURIComponent(provider)}/connect`),
    disconnect: (provider: string) =>
      api.post<{ readonly disconnected: boolean }>(
        `integrations/${encodeURIComponent(provider)}/disconnect`,
      ),
  }
}

/**
 * src/modules/billing/controllers/billing.controller.ts. `usage` is the plan, period and usage
 * against each limit (RecoupSlots P8); `checkoutSession` opens a Stripe-hosted Checkout for one
 * public plan (P9, test mode until launch). Without a payments provider the actions answer
 * `PAYMENTS_UNAVAILABLE`, which the page renders as unavailable rather than as a failing button.
 */
export function billingEndpoints(api: ApiClient) {
  return {
    subscription: () => api.get<Subscription>('billing/subscription'),
    usage: () => api.get<BillingUsage>('billing/usage'),
    checkoutSession: (plan: CheckoutPlan) =>
      api.post<{ readonly url: string }>('billing/checkout-session', { plan }),
    portalSession: () => api.post<{ readonly url: string }>('billing/portal-session'),
    invoices: (limit = 10) => api.get<unknown>('billing/invoices', { query: { limit } }),
  }
}

/** src/modules/audit/controllers/audit-log.controller.ts — newest first, keyset by `occurred_before`. */
export function auditLogEndpoints(api: ApiClient) {
  return {
    list: (query: ListAuditLogQuery = {}) =>
      api.get<readonly AuditLogEntry[]>('audit-log', { query: { ...query } }),
  }
}
